import { Component, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { ParentDisclosureService } from '../../services/parent-disclosure.service';
import { DisclosureRequest } from '../../models/disclosure.model';

const DISMISS_STORAGE_KEY = 'disclosure-approval-banner-dismissed-ids';

@Component({
  selector: 'app-disclosure-approval-banner',
  standalone: true,
  imports: [TranslatePipe],
  templateUrl: './disclosure-approval-banner.component.html',
  styleUrl: './disclosure-approval-banner.component.scss',
  host: {
    '[class.disclosure-approval-banner-host--visible]': 'show()',
  },
})
export class DisclosureApprovalBannerComponent {
  #disclosure = inject(ParentDisclosureService);
  #router = inject(Router);

  readonly dismissed = signal(false);

  readonly pending = computed(() => this.#disclosure.pending());
  readonly pendingIdsKey = computed(() =>
    this.pending()
      .map((item: DisclosureRequest) => item.id ?? '')
      .filter(Boolean)
      .sort()
      .join(','),
  );

  readonly show = computed(() => {
    if (!this.#disclosure.enabled() || this.dismissed() || this.pending().length === 0) {
      return false;
    }
    if (
      this.#router.url.includes('/identity/disclosure-approvals') ||
      this.#router.url.includes('/identity/pending-approvals')
    ) {
      return false;
    }
    try {
      return sessionStorage.getItem(DISMISS_STORAGE_KEY) !== this.pendingIdsKey();
    } catch {
      return true;
    }
  });

  readonly messageParams = computed(() => ({
    count: this.pending().length,
  }));

  readonly messageKey = computed(() =>
    this.pending().length > 1
      ? 'app.identity.disclosure.banner.multiple'
      : 'app.identity.disclosure.banner.single',
  );

  dismiss(): void {
    try {
      sessionStorage.setItem(DISMISS_STORAGE_KEY, this.pendingIdsKey());
    } catch {
      // ignore storage errors
    }
    this.dismissed.set(true);
  }

  openApprovals(): void {
    void this.#router.navigate(['/identity/pending-approvals']);
  }
}
